import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  doc, 
  updateDoc, 
  addDoc, 
  serverTimestamp 
} from "firebase/firestore";
import "./RevenuePanel.css"; 

const RevenuePanel = () => {
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [userFormData, setUserFormData] = useState({ 
    revenue: "", 
    monthlyRevenue: "",
    activeSubscriptions: "",
    pendingPayout: "",
    conversionRate: "",
    arpu: "",
    domain: "" 
  });

  useEffect(() => {
    if (isUserModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [isUserModalOpen]);

  useEffect(() => {
    fetchUserRevenue();
  }, []);

  const fetchUserRevenue = async () => {
    setLoading(true);
    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("role", "!=", "admin"));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        // Keep as displayable strings or numbers
        revenue: doc.data().revenue || 0,
        domain: doc.data().domain || "N/A"
      }));
      setRevenueData(data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const openUserEditModal = (user) => {
    setSelectedUser(user);
    setUserFormData({
      revenue: user.revenue || "",
      monthlyRevenue: user.monthlyRevenue || "",
      activeSubscriptions: user.activeSubscriptions || "",
      pendingPayout: user.pendingPayout || "",
      conversionRate: user.conversionRate || "",
      arpu: user.arpu || "",
      domain: user.domain || "" 
    });
    setIsUserModalOpen(true);
  };

  const handleUserInputChange = (e) => {
    const { name, value } = e.target;
    // All inputs are now treated as text/strings
    setUserFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const saveUserRevenue = async (e) => {
    e.preventDefault();
    try {
      const userRef = doc(db, "users", selectedUser.id);
      
      await updateDoc(userRef, {
        revenue: userFormData.revenue,
        monthlyRevenue: userFormData.monthlyRevenue,
        activeSubscriptions: userFormData.activeSubscriptions,
        pendingPayout: userFormData.pendingPayout,
        conversionRate: userFormData.conversionRate,
        arpu: userFormData.arpu,
        // domain is not updated here because it's read-only in the UI, 
        // but we include it if you want to ensure it stays synced
        domain: userFormData.domain 
      });

      await addDoc(collection(db, "notifications"), {
        recipientId: selectedUser.id,
        title: "Financial Update",
        message: "An administrator has updated your revenue analytics.",
        type: "revenue_update",
        isRead: false,
        createdAt: serverTimestamp()
      });

      setIsUserModalOpen(false);
      fetchUserRevenue();
      alert("User Records Updated!");
    } catch (err) {
      console.error("DETAILED ERROR:", err.code, err.message);
      alert(`Error: ${err.message}`); 
    }
  };
  
  if (loading) return <div className="loading-spinner">Loading Financial Data...</div>;

  return (
    <div className="revenue-container">
      <div className="revenue-header">
        <h2>💰 Financial Management</h2>
      </div>

      <div className="revenue-table-card">
        <table className="revenue-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Email</th>
              <th>Domain</th>
              <th>Total Revenue</th>
              <th>Status</th>
              <th style={{ textAlign: "center" }}>Edit Data</th>
            </tr>
          </thead>
          <tbody>
            {revenueData.map((item) => (
              <tr key={item.id}>
                <td><strong>{item.name}</strong></td>
                <td className="email-cell">{item.email || "N/A"}</td>
                <td className="domain-cell">
                  <span className="domain-badge">
                    {item.domain}
                  </span>
                </td>
                <td className="amount-cell">${Number(item.revenue).toFixed(2)}</td>
                <td>
                  <span className={`rev-status ${item.status?.replace(/\s+/g, '-') || 'active'}`}>
                    {item.status || "Active"}
                  </span>
                </td>
                <td style={{ textAlign: "center" }}>
                  <button className="row-edit-icon" onClick={() => openUserEditModal(item)}>✏️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isUserModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content standard-modal">
            <div className="modal-header">
              <h3>Update Analytics: {selectedUser?.name}</h3>
              <button className="close-modal" onClick={() => setIsUserModalOpen(false)}>&times;</button>
            </div>
            
            <form onSubmit={saveUserRevenue} className="standard-form">
              <div className="form-grid">
                {/* Domain Input Field - Marked as readOnly so it can't be edited */}
                <div className="form-group" style={{ gridColumn: "span 2" }}>
                  <label>Domain Name (View Only)</label>
                  <input 
                    type="text" 
                    name="domain" 
                    className="readonly-input"
                    value={userFormData.domain} 
                    readOnly 
                    style={{ backgroundColor: "#f4f4f4", cursor: "not-allowed" }}
                  />
                </div>
                
                <div className="form-group">
                  <label>ESTIMATED EARNINGS ($)</label>
                  <input type="text" name="revenue" value={userFormData.revenue} onChange={handleUserInputChange} />
                </div>
                <div className="form-group">
                  <label>PAGE RPM ($)</label>
                  <input type="text" name="monthlyRevenue" value={userFormData.monthlyRevenue} onChange={handleUserInputChange} />
                </div>
                <div className="form-group">
                  <label>PAGE VIEWS</label>
                  <input type="text" name="activeSubscriptions" value={userFormData.activeSubscriptions} onChange={handleUserInputChange} />
                </div>
                <div className="form-group">
                  <label>CLICKS</label>
                  <input type="text" name="pendingPayout" value={userFormData.pendingPayout} onChange={handleUserInputChange} />
                </div>
                <div className="form-group">
                  <label>PAGE CTR (%)</label>
                  <input type="text" name="conversionRate" value={userFormData.conversionRate} onChange={handleUserInputChange} />
                </div>
                <div className="form-group">
                  <label>AD IMPRESSIONS</label>
                  <input type="text" name="arpu" value={userFormData.arpu} onChange={handleUserInputChange} />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setIsUserModalOpen(false)}>Cancel</button>
                <button type="submit" className="save-btn">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenuePanel;